import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { SocketIoService } from '../../shared/services/socket-io/socket-io.service';
import {
	CallParty,
	ContactGroup,
	ContactReference,
	ContactSource,
	ExtensionContact,
	ManualContact
} from '../interfaces/contact.interface';
import { ContactUtils } from '../utils/contact-utils';
import {
	EXTENSION_CONTACTS_CONSTANTS,
	IPhoneStateUpdateEvent,
	IGetPhoneStateResponse
} from '../utils/extension-contacts-utils';
import {
	ModelService,
	PluginCommunicationService,
	PluginRegistry,
	IAstimaxContactManualContacts,
	ICreateAstimaxContactManualContacts,
	IUpdateAstimaxContactManualContacts
} from 'core-client/api';
import { IContactWidget } from '../interfaces/contact-widget.interface';

interface ContactWidgetState {
  data: ContactWidgetData;
  update$: BehaviorSubject<ContactWidgetData | null>;
  pendingRequests: Set<string>;
  destroy$: Subject<void>;
  subscribedPhoneIds: string[] | null;
}

export interface ContactWidgetData {
  widgetId: string;
  extensionContacts: ExtensionContact[];    // receiving via socket
  manualContacts: ManualContact[];          // receiving via db
  // Future contact sources can be added here:
  // ldapContacts: ExternalContact[];       // LDAP directory contacts
  // outlookContacts: ExternalContact[];    // Outlook/Exchange contacts
  contactGroups: ContactGroup[];            // contact groups with assigned contacts from all available contact sources
  subscribedExtensionPhoneIds: string[];    // subscribed phone IDs from extensionContacts
  lastUpdated: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ContactWidgetService {
  private pluginEventSender: ReturnType<PluginCommunicationService['createSender']>;

  // Consolidated per-widget runtime state
  private readonly widgetStateMap: Map<string, ContactWidgetState> = new Map<string, ContactWidgetState>();

	// Store the current user's phone number (extracted from sipUser)
	private currentUserPhoneNumber: string | undefined;

  constructor(private readonly socketService: SocketIoService, private readonly modelService: ModelService, private readonly pluginCommunicationService: PluginCommunicationService) {
    console.log('[ContactWidgetService] Service initialized');
    // create a plugin event sender with pre-bound 'from' set to 'astimax' (name of the plugin)
    this.pluginEventSender = this.pluginCommunicationService.createSender('astimax');

	  // Extract and store the user's phone number
	  const user = this.modelService._user.getUser();
	  if (user) {
		  this.currentUserPhoneNumber = ContactUtils.getUserPhoneNumber(user);
		  console.log('[ContactWidgetService] Current user phone number:', this.currentUserPhoneNumber);
	  }
  }

  /**
   * Initialize a widget in the service
   */
  initializeWidget(widgetId: string, dashboardId: string): void {
    console.log(`[ContactWidgetService] Initializing widget: ${widgetId}`);

    if (this.widgetStateMap.has(widgetId)) {
      console.warn(`[ContactWidgetService] Widget ${widgetId} already initialized — cleaning up first`);
      this.cleanupWidget(widgetId);
    }

    // Create initial widget data
    const initialData: ContactWidgetData = {
      widgetId,
      extensionContacts: [],
      manualContacts: [],
      contactGroups: [],
      subscribedExtensionPhoneIds: [],
      lastUpdated: new Date()
    };

    // Create consolidated widget state
    const state: ContactWidgetState = {
      data: initialData,
      update$: new BehaviorSubject<ContactWidgetData | null>(initialData),
      pendingRequests: new Set<string>(),
      destroy$: new Subject<void>(),
      subscribedPhoneIds: null,
    };
    this.widgetStateMap.set(widgetId, state);

    // Register widget with socket service
    this.socketService.registerWidget(widgetId, dashboardId);
    this.socketService.registerUnregisterCallback(widgetId, () => {
      console.log(`[ContactWidgetService] Socket service unregistered widget ${widgetId} — cleaning local state`);
      this.cleanupLocalState(widgetId);
    });

    // Setup socket event listeners for this widget
    this.setupExtensionContactEventListeners(widgetId);
  }

  /**
   * Get connection state from a socket service
   */
  getConnectionState(): Observable<'disconnected' | 'connecting' | 'connected'> {
    return this.socketService.getConnectionState();
  }

  /**
   * Get widget data stream for reactive updates
   */
  getWidgetDataStream(widgetId: string): Observable<ContactWidgetData> {
    const state = this.widgetStateMap.get(widgetId);
    if (!state) {
      console.error(`[ContactWidgetService] No update subject found for widget: ${widgetId}`);
      return EMPTY;
    }

    return state.update$.asObservable().pipe(
        filter((data: ContactWidgetData | null): data is ContactWidgetData => data !== null)
    );
  }

  /**
   * Get current widget data synchronously
   */
  getWidgetData(widgetId: string): ContactWidgetData | undefined {
    return this.widgetStateMap.get(widgetId)?.data;
  }

  /**
   * Load widget settings from database (called when socket connects)
   * This method focuses on requesting extension contacts for already loaded groups
   */
  loadExtensionContactFromSettings(widgetId: string): void {
    console.log(`[ContactWidgetService] Loading extension contact data for widget: ${widgetId}`);

    const state = this.widgetStateMap.get(widgetId);
    if (!state) {
      console.error(`[ContactWidgetService] Widget ${widgetId} not initialized`);
      return;
    }
    const widgetData = state.data;

    // Get phone IDs from existing contact groups (loaded during initialization)
    const phoneIds = this.getPhoneIdsFromGroups(widgetData.contactGroups);
    console.log(`[ContactWidgetService] Found phoneIds for widget ${widgetId}: ${JSON.stringify(phoneIds)}`);

    // Update subscribed phone IDs for this widget
    widgetData.subscribedExtensionPhoneIds = phoneIds;

    // Request extension contact data if we have phone IDs
    if (phoneIds.length > 0) {
      this.requestExtensionContacts(widgetId, phoneIds);
      // Also subscribe to ongoing updates
      this.subscribeToExtensionContacts(widgetId, phoneIds);
    } else {
      console.log(`[ContactWidgetService] No phone IDs to request for widget ${widgetId}`);
    }

    widgetData.lastUpdated = new Date();
    this.emitWidgetUpdate(widgetId, widgetData);
  }

	async loadAllManualContactsFromUser(widgetId: string) {
		const user = this.modelService._user.getUser();

		const astimaxManualContacts = await this.modelService._plugin_user_data.getAllManualContacts(user._id);

		const manualContacts = astimaxManualContacts.map((contact: IAstimaxContactManualContacts) => ({
			contactId: contact.contact_id,
			source: ContactSource.MANUAL,
			name: contact.name,
			phoneNumber: contact.phone_number,
			initial: ContactUtils.getInitials(contact.name),
			isPublic: contact.is_public,
			userId: contact.user_id,
			created: contact.createdAt,
			updated: contact.updatedAt
		})) as ManualContact[];

		console.log(`[Contact Widget ${widgetId}] Loading ${manualContacts.length} manual contacts from settings`);
		this.updateManualContacts(widgetId, [...manualContacts], false);
	}

  /**
   * Update contact groups for a specific widget with automatic order migration
   */
  updateContactGroups(widgetId: string, contactGroups: ContactGroup[]): void {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData) {
      console.error(`[ContactWidgetService] Widget ${widgetId} not found`);
      return;
    }

    console.log(`[ContactWidgetService] Updating contact groups for widget ${widgetId}: ${contactGroups.length} groups`);

    // Check if any groups need order migration
    let migratedGroups = contactGroups;
    let needsMigration = false;

    contactGroups.forEach(group => {
      if (ContactUtils.needsOrderMigration(group)) {
        needsMigration = true;
      }
    });

    // Perform migration if needed
    if (needsMigration) {
      console.log(`[ContactWidgetService] Migrating contact groups to include order numbers for widget ${widgetId}`);
      migratedGroups = ContactUtils.migrateContactGroupsToOrdered(contactGroups);
    } else {
      // Ensure all contact groups have properly ordered contacts
      migratedGroups = contactGroups.map(group => ({
        ...group,
        contacts: this.ensureContactOrder(group.contacts)
      }));
    }

    widgetData.contactGroups = [...migratedGroups];
    widgetData.lastUpdated = new Date();

    // Update extension contact subscriptions based on new groups
    const newPhoneIds = this.getPhoneIdsFromGroups(migratedGroups);

    // Only update subscription if the widget already had subscribed phoneIds.
    // On first load, loadExtensionContactFromSettings() handles the initial subscribe+request
    // when the onSocketConnected handler fires.
    if (this.socketService.ioSocket.connected && widgetData.subscribedExtensionPhoneIds.length > 0) {
      this.updateExtensionContactSubscription(widgetId, newPhoneIds);
    }

    this.emitWidgetUpdate(widgetId, widgetData);

    // If migration occurred, log completion
    if (needsMigration) {
      console.log(`[ContactWidgetService] Order migration completed for widget ${widgetId}`);
    }
  }

  /**
   * Update manual contacts for a specific widget
   */
  updateManualContacts(widgetId: string, manualContacts: ManualContact[], updateTimestamp: boolean): void {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData) {
      console.error(`[ContactWidgetService] Widget ${widgetId} not found`);
      return;
    }

    console.log(`[ContactWidgetService] Updating manual contacts for widget ${widgetId}: ${manualContacts.length} contacts`);

    widgetData.manualContacts = [...manualContacts];
    if (updateTimestamp) {
      widgetData.lastUpdated = new Date();
    }

    this.emitWidgetUpdate(widgetId, widgetData);
  }

	async createAstimaxContactManualContact(name: string, phoneNumber: string): Promise<{ manualContact: ManualContact | undefined; success: boolean }> {
		try {
			const user = this.modelService._user.getUser();
			const newManualContact: ICreateAstimaxContactManualContacts = {
				name: name.trim(),
				phone_number: phoneNumber.trim(),
				user_id: user._id,
				is_public: false
			};

			const astimaxManualContact = await this.modelService._plugin_user_data.upsertManualContact(newManualContact.user_id, newManualContact);

			if ('contact_id' in astimaxManualContact) {
				const manualContact = {
					contactId: astimaxManualContact.contact_id,
					source: ContactSource.MANUAL,
					name: astimaxManualContact.name,
					phoneNumber: astimaxManualContact.phone_number,
					initial: ContactUtils.getInitials(astimaxManualContact.name),
					isPublic: astimaxManualContact.is_public,
					userId: astimaxManualContact.user_id,
					created: astimaxManualContact.createdAt,
					updated: astimaxManualContact.updatedAt
				} as ManualContact;

				return {
					manualContact: manualContact,
					success: true
				}
			} else {
				console.error(`Error creating manual contact: ${name}. Missing contact_id in response.`,astimaxManualContact);
				return {
					manualContact: undefined,
					success: true
				}
			}
		} catch (error) {
			console.error(`Error creating manual contact "${name}":`, error);
			return {
				manualContact: undefined,
				success: false
			};
		}
	}

	async updateAstimaxContactManualContact(contact: ManualContact, updates: Partial<Pick<ManualContact, 'name' | 'phoneNumber'>>): Promise<{ manualContact: ManualContact | undefined; success: boolean }> {
		try {
			const user = this.modelService._user.getUser();

			const updatedAstimaxContactManualContact: IUpdateAstimaxContactManualContacts = {
				contact_id: contact.contactId,
				name: updates.name || contact.name,
				phone_number: updates.phoneNumber || contact.phoneNumber,
				user_id: user._id,
				is_public: contact.isPublic
			};

			const astimaxManualContact = await this.modelService._plugin_user_data.upsertManualContact(updatedAstimaxContactManualContact.user_id, updatedAstimaxContactManualContact, updatedAstimaxContactManualContact.contact_id);

			if ('contact_id' in astimaxManualContact) {
				const updated: ManualContact = {
					...contact,
					...updates,
					updated: astimaxManualContact.updatedAt
				};

				if (updates.name) {
					updated.initials = ContactUtils.getInitials(updates.name);
				}

				return {
					manualContact: updated,
					success: true
				};
			} else {
				console.error(`Error updating manual contact: ${contact.name}. Missing contact_id in response.`,astimaxManualContact);
				return {
					manualContact: undefined,
					success: false
				}
			}
		} catch (error) {
			console.error(`Error updated manual contact "${contact.name}":`, error);
			return {
				manualContact: undefined,
				success: false
			};
		}
	}

	async deleteAstimaxContactManualContact(contactToDelete: ManualContact): Promise<boolean> {
		console.log(`[ContactWidgetService] Deleting manual contact ${contactToDelete.contactId}`);
		try {
			const user = this.modelService._user.getUser();
			const result = await this.modelService._plugin_user_data.deleteManualContactById(user._id, contactToDelete.contactId);
			if (result?.success) {
				console.log(`[ContactWidgetService] Successfully deleted contact ${contactToDelete.contactId}`);
				return true;
			} else {
				console.warn(`[ContactWidgetService] Failed to delete contact ${contactToDelete.contactId}:`, result);
				return false;
			}
		} catch (error) {
			console.error(`[ContactWidgetService] Error deleting contact ${contactToDelete.contactId}:`, error);
			return false;
		}
	}

  /**
   * Save widget settings to a database with migration handling
   * Save manual contacts information and the corresponding group assignment,
   * if a manual contact or, for example, an extension contact has been assigned to a group.
   */
  async saveContactWidgetSettingsToDatabase(widgetId: string, contactWidget: IContactWidget): Promise<void> {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData) {
      console.error(`[ContactWidgetService] Widget ${widgetId} not found`);
      return;
    }

    // Use the current widget data (which may have been migrated)
    contactWidget.settings.contactGroups = widgetData.contactGroups;

    try {
      console.log(`[ContactWidgetService] Saving settings for widget ${widgetId}`, contactWidget);
      await this.modelService._appPluginWidget.upsertOne(contactWidget);
      console.log(`[ContactWidgetService] Settings saved for widget ${widgetId}`);
    } catch (error) {
      console.error(`[ContactWidgetService] Error saving settings for widget ${widgetId}:`, error);
      throw error;
    }
  }

  /**
   * Check if widget settings need migration and perform automatic migration
   */
  async checkAndMigrateWidgetSettings(widgetId: string, contactWidget: IContactWidget): Promise<boolean> {
    let needsSaving = false;

    if (contactWidget?.settings?.contactGroups && contactWidget.settings.contactGroups.length > 0) {
      // Check if any groups need order migration
      const needsMigration = contactWidget.settings.contactGroups.some((group: ContactGroup) =>
          ContactUtils.needsOrderMigration(group)
      );

      if (needsMigration) {
        console.log(`[ContactWidgetService] Widget ${widgetId} needs order migration`);

        // Perform migration
        const migratedGroups = ContactUtils.migrateContactGroupsToOrdered(contactWidget.settings.contactGroups);

        // Update widget settings
        contactWidget.settings.contactGroups = migratedGroups;

        // Update service data
        this.updateContactGroups(widgetId, migratedGroups);

        // Save to database
        await this.saveContactWidgetSettingsToDatabase(widgetId, contactWidget);

        console.log(`[ContactWidgetService] Order migration completed and saved for widget ${widgetId}`);
        needsSaving = true;
      }
    }

    return needsSaving;
  }

  /**
   * Request extension contacts for a widget
   */
  requestExtensionContacts(widgetId: string, phoneIds: string[]): void {
    // Ensure phoneIds are unique
    const uniquePhoneIds = [...new Set(phoneIds)];

    if (uniquePhoneIds.length === 0) {
      console.log(`[ContactWidgetService] No phone IDs to request for widget ${widgetId}`);
      return;
    }

    const requestId = ContactUtils.generateUUID();
    const payload = {
      id: requestId,
      type: EXTENSION_CONTACTS_CONSTANTS.TYPE,
      service: EXTENSION_CONTACTS_CONSTANTS.SERVICE,
      method: EXTENSION_CONTACTS_CONSTANTS.REQUEST_PHONE_STATE,
      payload: {
        phoneIds: uniquePhoneIds
      },
      metadata: {
        timestamp: new Date().toISOString(),
        version: EXTENSION_CONTACTS_CONSTANTS.VERSION,
      }
    };

    // Track this request for this specific widget
    const pendingRequestsForWidget = this.widgetStateMap.get(widgetId)?.pendingRequests;
    if (pendingRequestsForWidget) {
      pendingRequestsForWidget.add(requestId);
    }

    console.log(`[ContactWidgetService] Requesting extension contacts for widget ${widgetId} with requestId: ${requestId}:
    phoneIds: ${JSON.stringify(uniquePhoneIds)} (${phoneIds.length - uniquePhoneIds.length} duplicates removed)`);

    // Emit with widget-specific context
    this.socketService.emitEvent(EXTENSION_CONTACTS_CONSTANTS.EMIT_REQUEST, payload, widgetId);

    // Listen for extension contact responses (one-time) with proper filtering based on request ID
    this.socketService.onEvent(EXTENSION_CONTACTS_CONSTANTS.LISTEN_ON_RESPONSE, widgetId)
    .pipe(
        filter((event: unknown) => {
          const e = event as IGetPhoneStateResponse;

	        return (e?.id === requestId &&
		        (e?.type === EXTENSION_CONTACTS_CONSTANTS.LISTEN_ON_RESPONSE &&
			        e?.method === EXTENSION_CONTACTS_CONSTANTS.EVENT_TYPE
		        ));
        }),
        takeUntil(this.widgetStateMap.get(widgetId)?.destroy$ ?? EMPTY),
        take(1)
    )
    .subscribe((data: unknown) => {
      const response = data as IGetPhoneStateResponse;
      console.log(`[ContactWidgetService] Received extension contact response to requestId: ${response.id} for widget ${widgetId}.`);

      // Remove from pending requests
      const pendingRequestsForWidget = this.widgetStateMap.get(widgetId)?.pendingRequests;
      if (pendingRequestsForWidget) {
        pendingRequestsForWidget.delete(requestId);
      }

      this.handleExtensionContactResponse(widgetId, response);
    });
  }

  /**
   * Request ALL available extension contacts (for dialogs)
   */
  requestAllExtensionContacts(widgetId: string): Observable<ExtensionContact[]> {
    const requestId = ContactUtils.generateUUID();
    const payload = {
      id: requestId,
      type: EXTENSION_CONTACTS_CONSTANTS.TYPE,
      service: EXTENSION_CONTACTS_CONSTANTS.SERVICE,
      method: EXTENSION_CONTACTS_CONSTANTS.REQUEST_PHONE_STATE,
      payload: {
        phoneIds: [] // Empty array requests all available contacts
      },
      metadata: {
        timestamp: new Date().toISOString(),
        version: EXTENSION_CONTACTS_CONSTANTS.VERSION,
      }
    };

    console.log(`[ContactWidgetService] Requesting ALL extension contacts for widget ${widgetId}, requestId: ${requestId}`);
    this.socketService.emitEvent(EXTENSION_CONTACTS_CONSTANTS.EMIT_REQUEST, payload, widgetId);

    // Return observable that emits when response is received
    return this.socketService.onEvent(EXTENSION_CONTACTS_CONSTANTS.LISTEN_ON_RESPONSE, widgetId)
    .pipe(
        filter((event: unknown) => {
          const e = event as IPhoneStateUpdateEvent;
          // Only process responses that match our request ID
	        return (e?.id === requestId &&
		        e.type === EXTENSION_CONTACTS_CONSTANTS.LISTEN_ON_RESPONSE &&
		        e?.method === EXTENSION_CONTACTS_CONSTANTS.EVENT_TYPE);
        }),
        take(1), // Take the first response that matches our filter
        map((event: unknown) => {
          const response = event as IPhoneStateUpdateEvent;
          console.log(`[ContactWidgetService] Received ALL extension contacts to requestId: ${response.id} for widget ${widgetId}.`);
          if (response?.payload && Array.isArray(response.payload)) {
            return response.payload.map((contact: ExtensionContact) => ({
              ...contact,
              source: ContactSource.EXTENSION,
	            contactId: contact.id?.toString()
            }));
          }
          return [];
        })
    );
  }

  /**
   * Initiate a call for a specific widget
   */
  initiateCall(widgetId: string, contactId: string, phoneNumber: string): void {
    console.log(`[ContactWidgetService] Initiating call from widget ${widgetId} to ${phoneNumber}`);

    // send "START_CALL" action to phone module of Astimax plugin
    this.pluginEventSender(PluginRegistry.Astimax.Actions.PhoneModule.START_CALL, {
      targetNumber: phoneNumber,
      withVideo: false
    });
  }

	/**
	 * Transfer an active call to another contact
	 */
	transferCall(widgetId: string, fromContactId: string, toContactId: string, toPhoneNumber: string, callParty: CallParty
	): void {
		console.log(`[ContactWidgetService] Transferring call from widget ${widgetId}:`, {
			from: fromContactId,
			to: toContactId,
			toPhoneNumber: toPhoneNumber,
			callParty: callParty
		});

		// Send "TRANSFER_CALL" action to phone module of Astimax plugin
		this.pluginEventSender(PluginRegistry.Astimax.Actions.PhoneModule.BLIND_TRANSFER_CALL, {
			targetNumber: toPhoneNumber
		});
	}

  private cleanupLocalState(widgetId: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (state) {
      this.unsubscribeFromExtensionContacts(widgetId);
      state.destroy$.next();
      state.destroy$.complete();
      state.update$.complete();
      this.widgetStateMap.delete(widgetId);
    }
  }

  cleanupWidget(widgetId: string): void {
    console.log(`[ContactWidgetService] Cleaning up widget: ${widgetId}`);
    this.cleanupLocalState(widgetId);
    this.socketService.unregisterWidget(widgetId);
  }

  /**
   * Get count of active widgets
   */
  getActiveWidgetCount(): number {
    return this.widgetStateMap.size;
  }

  /**
   * Get debug information with phoneId analysis per widget
   */
  getDebugInfo(): { [widgetId: string]: ContactWidgetData & { phoneIdAnalysis: { total: number, unique: number, duplicates: string[] } } } {
    const info: { [widgetId: string]: ContactWidgetData & { phoneIdAnalysis: { total: number, unique: number, duplicates: string[] } } } = {};

    for (const [widgetId, { data }] of this.widgetStateMap.entries()) {
      // Analyze phoneIds for duplicates within this widget
      const allPhoneIds = this.getPhoneIdsFromGroups(data.contactGroups);
      const uniquePhoneIds = [...new Set(allPhoneIds)];
      const duplicates = allPhoneIds.filter((id: string, index: number) => allPhoneIds.indexOf(id) !== index);

      info[widgetId] = {
        ...data,
        phoneIdAnalysis: {
          total: allPhoneIds.length,
          unique: uniquePhoneIds.length,
          duplicates: [...new Set(duplicates)] // Remove duplicate duplicates for cleaner output
        }
      };
    }

    return info;
  }

  // Private helper methods

  /**
   * Ensures contact references have proper order numbers
   */
  private ensureContactOrder(contacts: ContactReference[]): ContactReference[] {
    // Check if all contacts have order numbers
    const hasOrderNumbers = contacts.every((contact: ContactReference) => contact.order !== undefined);

    if (hasOrderNumbers) {
      // Sort by existing order numbers
      return ContactUtils.sortContactReferences(contacts);
    } else {
      // Assign order numbers based on current array position
      return ContactUtils.assignOrderNumbers(contacts);
    }
  }

  /**
   * Emit widget update to subscribers
   */
  private emitWidgetUpdate(widgetId: string, widgetData: ContactWidgetData): void {
    const state = this.widgetStateMap.get(widgetId);
    if (state) {
      state.update$.next({ ...widgetData }); // Send copy to avoid mutations
    }
  }

  /**
   * Setup socket listeners for extension contacts for a specific widget
   */
  private setupExtensionContactEventListeners(widgetId: string): void {
    console.log(`[ContactWidgetService] Setting up event listeners for extension contacts for widget: ${widgetId}`);

    const state = this.widgetStateMap.get(widgetId);
    if (!state) return;

    // On disconnect: all server-side subscriptions are lost for this socket connection.
    // Reset local state so the reconnect guard allows re-subscription when the socket reconnects.
    this.socketService.getConnectionState().pipe(
      takeUntil(state.destroy$),
    ).subscribe((connectionState) => {
      if (connectionState === 'disconnected' && state.subscribedPhoneIds !== null) {
        console.log(`[ContactWidgetService] Socket disconnected — clearing server subscription state for widget ${widgetId}`);
        state.subscribedPhoneIds = null;
      }
    });

    // Listen for extension contact events (ongoing updates) with proper widget filtering
    this.socketService.onServiceEvent<IPhoneStateUpdateEvent>(
      EXTENSION_CONTACTS_CONSTANTS.LISTEN_ON_EVENT,
      widgetId,
      EXTENSION_CONTACTS_CONSTANTS.SERVICE,
      EXTENSION_CONTACTS_CONSTANTS.LISTEN_UPDATE
    )
    .pipe(
        filter((event: IPhoneStateUpdateEvent) => {
          // Filter based on the phone IDs that this widget has subscribed to
          const widgetData = this.widgetStateMap.get(widgetId)?.data;
          if (widgetData && event?.payload && Array.isArray(event.payload)) {
            const responsePhoneIds = event.payload
            .map((contact: ExtensionContact) => contact.id?.toString())
            .filter(Boolean);

            const hasMatchingPhoneIds = responsePhoneIds.some((phoneId: string) =>
                widgetData.subscribedExtensionPhoneIds.includes(phoneId)
            );

            if (!hasMatchingPhoneIds) {
              console.log(`[ContactWidgetService] Ignoring event for widget ${widgetId} - no matching phone IDs.
              Widget subscribed to: ${JSON.stringify(widgetData.subscribedExtensionPhoneIds)}, Event contains: ${JSON.stringify(responsePhoneIds)}`);
              return false;
            }

            return true;
          }

          return false; // Ignore if we can't determine specificity
        }),
        takeUntil(state.destroy$)
    )
    .subscribe((data: unknown) => {
      const response = data as IPhoneStateUpdateEvent;
      console.log(`[ContactWidgetService] Received extension contact event for widget ${widgetId}:`, response);
      this.handleExtensionContactStatusUpdate(widgetId, response);
    });
  }

  /**
   * Update extension contact subscription for a widget
   */
  private updateExtensionContactSubscription(widgetId: string, newPhoneIds: string[]): void {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData) {
      return;
    }

    // Ensure phoneIds are unique
    const uniqueNewPhoneIds = [...new Set(newPhoneIds)];
    const oldPhoneIds = widgetData.subscribedExtensionPhoneIds;

    console.log(`[ContactWidgetService] Updating extension subscription for widget ${widgetId}:`, {
      old: oldPhoneIds,
      new: uniqueNewPhoneIds,
      duplicatesRemoved: newPhoneIds.length - uniqueNewPhoneIds.length
    });

    // Update subscribed phone IDs with unique values for this specific widget
    widgetData.subscribedExtensionPhoneIds = uniqueNewPhoneIds;

    // Subscribe to new contacts for this widget
    if (uniqueNewPhoneIds.length > 0) {
      this.subscribeToExtensionContacts(widgetId, uniqueNewPhoneIds);
      // Also request initial data for this widget
      this.requestExtensionContacts(widgetId, uniqueNewPhoneIds);
    }
  }

  private unsubscribeFromExtensionContacts(widgetId: string): void {
    const state = this.widgetStateMap.get(widgetId);
    if (!state || !state.subscribedPhoneIds) return;

    const phoneIds = state.subscribedPhoneIds;
    state.subscribedPhoneIds = null;

    console.log(`[ContactWidgetService] Unsubscribing from phoneState for widget ${widgetId}: ${JSON.stringify(phoneIds)}`);

    const requestId = ContactUtils.generateUUID();
    const payload = {
      id: requestId,
      type: EXTENSION_CONTACTS_CONSTANTS.TYPE,
      service: EXTENSION_CONTACTS_CONSTANTS.SERVICE,
      method: EXTENSION_CONTACTS_CONSTANTS.METHOD_UNSUBSCRIBE,
      payload: {
        eventType: EXTENSION_CONTACTS_CONSTANTS.EVENT_TYPE,
        metadata: { phoneIds },
      },
      metadata: {
        timestamp: new Date().toISOString(),
        version: EXTENSION_CONTACTS_CONSTANTS.VERSION,
        traceId: requestId,
        correlationId: requestId,
        noResponse: true,
      },
    };

    this.socketService.emitEvent(EXTENSION_CONTACTS_CONSTANTS.EMIT_REQUEST, payload, widgetId);
  }

  /**
   * Subscribe to extension contact updates for a widget
   */
  private subscribeToExtensionContacts(widgetId: string, phoneIds: string[]): void {
    // Ensure phoneIds are unique
    const uniquePhoneIds = [...new Set(phoneIds)];

    if (uniquePhoneIds.length === 0) {
      return;
    }

    const state = this.widgetStateMap.get(widgetId);
    if (!state) return;

    // Deduplication: skip if already subscribed to the same phoneIds
    if (state.subscribedPhoneIds &&
        uniquePhoneIds.length === state.subscribedPhoneIds.length &&
        uniquePhoneIds.every(id => state.subscribedPhoneIds!.includes(id))) {
      console.log(`[ContactWidgetService] Already subscribed to phoneIds for widget ${widgetId}, skipping`);
      return;
    }

    const requestId = ContactUtils.generateUUID();
    const payload = {
      id: requestId,
      type: EXTENSION_CONTACTS_CONSTANTS.TYPE,
      service: EXTENSION_CONTACTS_CONSTANTS.SERVICE,
      method: EXTENSION_CONTACTS_CONSTANTS.METHOD_SUBSCRIBE,
      payload: {
        eventType: EXTENSION_CONTACTS_CONSTANTS.EVENT_TYPE,
        metadata: {
          phoneIds: uniquePhoneIds,
        }
      },
      metadata: {
        timestamp: new Date().toISOString(),
        version: EXTENSION_CONTACTS_CONSTANTS.VERSION,
        traceId: ContactUtils.generateUUID(),
        correlationId: ContactUtils.generateUUID(),
        noResponse: true,
      }
    };

    console.log(`[ContactWidgetService] Subscribing to extension contacts for widget ${widgetId} with requestId: ${requestId}
     | phoneIds: ${JSON.stringify(uniquePhoneIds)} (${phoneIds.length - uniquePhoneIds.length} duplicates removed)`);
    this.socketService.emitEvent(EXTENSION_CONTACTS_CONSTANTS.EMIT_REQUEST, payload, widgetId);
    state.subscribedPhoneIds = uniquePhoneIds;
  }

  /**
   * Handle extension contact response for a specific widget
   */
  private handleExtensionContactResponse(widgetId: string, response: IGetPhoneStateResponse): void {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData || !response.payload || response.payload.length === 0) {
      console.warn(`[ContactWidgetService] Invalid response data for widget ${widgetId}:`, response);
      return;
    }

    console.log(`[ContactWidgetService] Setting extension contacts for widget ${widgetId}: ${response.payload.length} contacts`);

    // Filter the response to only include contacts that this widget has subscribed to
    const relevantContacts = response.payload.filter((contact: ExtensionContact) => {
      const contactPhoneId = contact.id?.toString();
      return contactPhoneId && widgetData.subscribedExtensionPhoneIds.includes(contactPhoneId);
    });

    console.log(`[ContactWidgetService] Filtered to ${relevantContacts.length} relevant contacts for widget ${widgetId} (subscribed to: ${JSON.stringify(widgetData.subscribedExtensionPhoneIds)})`);

	  // Set extension contacts for this specific widget
	  const contactsWithSource: ExtensionContact[] = relevantContacts.map((contact: ExtensionContact): ExtensionContact => ({
		  ...contact,
		  source: ContactSource.EXTENSION,
		  contactId: contact.id?.toString()
	  } as ExtensionContact));

	  // Mark the current user's contact
	  widgetData.extensionContacts = ContactUtils.markCurrentUserInExtensionContacts(
		  contactsWithSource,
		  this.currentUserPhoneNumber
	  );

	  // Log if we found the current user
	  const currentUserContact = widgetData.extensionContacts.find(c => c.isCurrentUser);
	  if (currentUserContact) {
		  console.log(`[ContactWidgetService] Current user found in extension contacts:`, currentUserContact);
	  } else {
		  console.log(`[ContactWidgetService] Current user not found in extension contacts. User phone: ${this.currentUserPhoneNumber}`);
	  }

    widgetData.lastUpdated = new Date();

    this.emitWidgetUpdate(widgetId, widgetData);
  }

  /**
   * Handle extension contact status update for a specific widget
   */
  private handleExtensionContactStatusUpdate(widgetId: string, response: IPhoneStateUpdateEvent): void {
    const widgetData = this.widgetStateMap.get(widgetId)?.data;
    if (!widgetData || !response.payload || !Array.isArray(response.payload)) {
      return;
    }

    console.log(`[ContactWidgetService] Updating extension contacts for widget ${widgetId}: ${response.payload.length} contacts in response`);

    // Filter the response to only include contacts that this widget has subscribed to
    const relevantUpdates = response.payload.filter((contact: ExtensionContact) => {
      const contactPhoneId = contact.id?.toString();
      return contactPhoneId && widgetData.subscribedExtensionPhoneIds.includes(contactPhoneId);
    });

    if (relevantUpdates.length === 0) {
      console.log(`[ContactWidgetService] No relevant updates for widget ${widgetId} (subscribed to: ${JSON.stringify(widgetData.subscribedExtensionPhoneIds)})`);
      return;
    }

    console.log(`[ContactWidgetService] Processing ${relevantUpdates.length} relevant updates for widget ${widgetId}`);

    // Update extension contacts with new data
	  const updatedContacts: ExtensionContact[] = relevantUpdates.map((contact: ExtensionContact): ExtensionContact => ({
		  ...contact,
		  source: ContactSource.EXTENSION,
		  contactId: contact.id?.toString()
	  } as ExtensionContact));

    // Merge with existing contacts (update existing, keep others)
    const existingContacts = widgetData.extensionContacts;
    const mergedContacts = existingContacts.map((existing: ExtensionContact) => {
      const update = updatedContacts.find((updated) => updated.contactId === existing.contactId);
	    if (update) {
		    // Preserve isCurrentUser flag when updating
		    return { ...existing, ...update, isCurrentUser: existing.isCurrentUser } as ExtensionContact;
	    }
	    return existing;
    });

    // Add new contacts that don't exist
    const newContacts = updatedContacts.filter(updated =>
        !existingContacts.some((existing: ExtensionContact) => existing.contactId === updated.contactId)
    );

	  // Mark current user in all contacts (including new ones)
	  widgetData.extensionContacts = ContactUtils.markCurrentUserInExtensionContacts(
		  [...mergedContacts, ...newContacts],
		  this.currentUserPhoneNumber
	  );

    widgetData.lastUpdated = new Date();

    this.emitWidgetUpdate(widgetId, widgetData);
  }

  /**
   * Get phone IDs from contact groups (ensures uniqueness per widget)
   */
  private getPhoneIdsFromGroups(contactGroups: ContactGroup[]): string[] {
    const phoneIds = contactGroups.flatMap((group: ContactGroup) =>
        group.contacts
        .filter((contact: ContactReference) => contact.source === ContactSource.EXTENSION)
        .map((contact: ContactReference) => contact.contactId.toString())
    );

    // Remove duplicates using Set and return as an array
    return [...new Set(phoneIds)];
  }
}
